import { type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import * as React from 'react';
import { PillFrame } from './PillPrimitives';

export interface SelectionPillWidgetProps {
  children: React.ReactNode;
  checked: boolean;
  onChange: () => void;
  tone?: keyof Theme['selectionPill']['tones'];
  type?: 'checkbox' | 'radio';
  name?: string;
  title?: string;
  showTone?: boolean;
}

export const SelectionPillWidget: React.FC<SelectionPillWidgetProps> = ({
  children,
  checked,
  onChange,
  tone,
  type = 'checkbox',
  name,
  title,
  showTone = false
}) => (
  <S.Label $selected={checked} $tone={tone} $showTone={showTone} title={title}>
    <S.Input $tone={tone} type={type} name={name} checked={checked} onChange={onChange} />
    {children}
  </S.Label>
);

namespace S {
  export const Input = styled.input<{ $tone?: keyof Theme['selectionPill']['tones'] }>`
    appearance: none;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin: 0;
    border: 1px solid currentColor;
    border-radius: 50%;
    color: inherit;
    background: transparent;
    cursor: inherit;
    display: grid;
    place-content: center;
    opacity: 0.55;

    &:checked {
      border-color: ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent};
      background: ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent};
      opacity: 1;
    }

    &:checked::after {
      content: '';
      width: 5px;
      height: 3px;
      margin-top: -2px;
      border-left: 1.5px solid ${(p) => p.theme.selectionPill.checkColor};
      border-bottom: 1.5px solid ${(p) => p.theme.selectionPill.checkColor};
      transform: rotate(-45deg);
    }
  `;

  export const Label = styled(PillFrame.withComponent('label'))<{
    $selected: boolean;
    $tone?: keyof Theme['selectionPill']['tones'];
    $showTone: boolean;
  }>`
    color: ${(p) =>
      p.$selected || p.$showTone
        ? p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent
        : p.theme.selectionPill.unselected.color};
    background: ${(p) =>
      p.$selected
        ? p.theme.selectionPill.tones[p.$tone ?? 'interface'].background
        : p.theme.selectionPill.unselected.background};
    border: 1px solid
      ${(p) =>
        p.$selected || p.$showTone
          ? p.theme.selectionPill.tones[p.$tone ?? 'interface'].border
          : p.theme.selectionPill.unselected.border};
    box-sizing: border-box;
    min-height: 36px;
    padding: 8px 12px;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    user-select: none;
    transition:
      border-color 120ms ease,
      background 120ms ease,
      color 120ms ease;

    &:hover {
      border-color: ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent};
      background: ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].background};
      color: ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent};
    }

    &:has(input:focus-visible) {
      outline: 2px solid ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].accent};
      outline-offset: 3px;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `;
}
